import { takeawaysPageConfig, type CompetencyTakeaway } from "../Data/takeawaysData";
import {competencyIcons} from '../Report/CompetencyIcons'
import { Lightbulb, CircleHelp, Zap } from "lucide-react";

interface TakeawaysPageProps {
  takeaways: [string, CompetencyTakeaway][];
  pageNumber: 1 | 2;
  reportData?: any;
}

const competencyAccents: Record<string, { gradient: string; iconBg: string; glowColor: string }> = {
  "Goal Setting": {
    gradient: "from-cyan/20 via-transparent to-transparent",
    iconBg: "bg-gradient-to-br from-slate-900 to-black border-primary/20 shadow-[inset_0_0_12px_hsla(190,95%,55%,0.15)]",
    glowColor: "hsla(190, 95%, 55%, 0.15)",
  },
  "Delegation": {
    gradient: "from-teal/20 via-transparent to-transparent",
    iconBg: "bg-gradient-to-br from-slate-900 to-black border-secondary/20 shadow-[inset_0_0_12px_hsla(175,80%,42%,0.15)]",
    glowColor: "hsla(175, 80%, 42%, 0.15)",
  },
  "Giving Feedback": {
    gradient: "from-purple/20 via-transparent to-transparent",
    iconBg: "bg-gradient-to-br from-slate-900 to-black border-accent/20 shadow-[inset_0_0_12px_hsla(260,60%,55%,0.15)]",
    glowColor: "hsla(260, 60%, 55%, 0.15)",
  },
  "Conflict Management": {
    gradient: "from-fuchsia-500/20 via-transparent to-transparent",
    iconBg: "bg-gradient-to-br from-slate-900 to-black border-purple-400/20 shadow-[inset_0_0_12px_hsla(320,70%,55%,0.15)]",
    glowColor: "hsla(320, 70%, 55%, 0.15)",
  },
};

export function TakeawaysPage({ takeaways, pageNumber,reportData }: TakeawaysPageProps) {
  const pageTakeaways = pageNumber === 1
    ? takeaways.slice(0, 2)
    : takeaways.slice(2, 4);

  return (
    <div className="report-page takeaways-bg p-10 md:p-16 overflow-hidden h-full flex flex-col">
      {/* Background elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-1/4 right-1/4 w-[500px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(circle, hsla(42, 90%, 55%, 0.06) 0%, transparent 60%)",
          }}
        />
        <div
          className="absolute bottom-1/3 left-1/4 w-[400px] h-[400px] rounded-full"
          style={{
            background: "radial-gradient(circle, hsla(190, 95%, 55%, 0.04) 0%, transparent 60%)",
          }}
        />
        <div className="absolute top-[12%] left-0 w-1/3 h-px bg-gradient-to-r from-gold/20 to-transparent" />
        <div className="absolute top-[12%] right-0 w-1/3 h-px bg-gradient-to-l from-gold/20 to-transparent" />
      </div>

      <div className="relative z-10 h-full flex flex-col">
        {/* Header - same on both pages */}
        <div className="text-center mb-8">
          <p className="text-base tracking-[0.3em] uppercase text-gold mb-3">{takeawaysPageConfig.sectionLabel}</p>
          <h2 className="text-5xl font-light tracking-wide">
            <span className="bg-gradient-to-r from-foreground via-foreground to-muted-foreground bg-clip-text text-transparent">
              {takeawaysPageConfig.title}
            </span>
          </h2>
          <p className="text-muted-foreground mt-3 mx-auto text-lg">
            {takeawaysPageConfig.subtitle}
          </p>
          <div className="w-24 h-0.5 mx-auto mt-4 bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
        </div>

        {/* Takeaways grid - 2 competencies per page */}
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {pageTakeaways.map(([competency, takeaway]) => {
            const accent = competencyAccents[competency] || competencyAccents["Goal Setting"];
            const IconComponent = competencyIcons[competency];

            return (
              <div
                key={competency}
                className="relative p-6 rounded-2xl bg-card/60 border border-border backdrop-blur-sm overflow-hidden flex flex-col"
              >
                {/* Gradient accent */}
                <div className={`absolute top-0 left-0 right-0 h-32 bg-gradient-to-b ${accent.gradient} pointer-events-none`} />

                {/* 1. Header: Icon + Title */}
                <div className="relative flex items-center gap-3 mb-5">
                  <div className={`w-10 h-10 rounded-xl ${accent.iconBg} border ring-1 ring-inset ring-white/5 flex items-center justify-center`}>
                    {IconComponent ? <IconComponent /> : <span className="text-lg">{takeaway.icon}</span>}
                  </div>
                  <h3 className="text-2xl font-medium text-foreground">{competency}</h3>
                </div>

                {/* 2. Core Idea */}
                <div className="relative mb-5">
                  <div className="p-4 rounded-xl bg-muted/40 border border-border/50">
                    <div className="flex items-center gap-2 mb-2">
                      <Lightbulb className="w-4 h-4 text-gold" />
                      <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground font-medium">Core Idea</span>
                    </div>
                    <p className="text-base text-foreground/90 italic leading-relaxed pl-[1.375rem]">
                      {takeaway.coreIdea}
                    </p>
                  </div>
                </div>

                {/* 3. Key Moves */}
                <div className="relative mb-5 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-3.5 h-3.5 text-primary" />
                    <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground font-medium">Key Moves</span>
                  </div>
                  <div className="space-y-2">
                    {takeaway.keyMoves.map((move, index) => (
                      <div key={index} className="flex items-start gap-3 pl-1">
                        <span className="text-sm text-muted-foreground mt-1 font-mono w-4 flex-shrink-0">{String(index + 1).padStart(2, '0')}</span>
                        <p className="text-base text-foreground/85 leading-relaxed">{move}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 4. Guiding Question */}
                <div className="relative">
                  <div className="p-3 rounded-lg border border-dashed border-border/60 bg-foreground/[0.02]">
                    <div className="flex items-center gap-2 mb-2">
                      <CircleHelp className="w-4 h-4 text-purple" />
                      <span className="text-sm tracking-[0.2em] uppercase text-muted-foreground font-medium">Guiding Question</span>
                    </div>
                    <p className="text-base text-muted-foreground italic leading-relaxed pl-[1.375rem]">
                      {takeaway.guidingQuestion}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
